import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import LaTeXRenderer from '../LaTeXRenderer';

const ManageMocks = () => {
  const { user } = useAuth();
  const [allTests, setAllTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive
  const [processingTest, setProcessingTest] = useState(null);

  useEffect(() => {
    fetchAllTests();
  }, []);

  const fetchAllTests = async () => {
    try {
      // Fetch all tests including inactive ones
      const response = await axios.get(`${API_BASE_URL}/api/tests?includeInactive=true`);
      setAllTests(response.data);
    } catch (error) {
      setError('Failed to load tests');
      console.error('Error fetching tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (testId, newStatus) => {
    setProcessingTest(testId);
    try {
      await axios.put(`${API_BASE_URL}/api/tests/${testId}`, {
        status: newStatus
      });
      
      // Update local state
      setAllTests(allTests.map(test => 
        test._id === testId ? { ...test, status: newStatus } : test
      ));
      
      alert(`Test status updated to ${newStatus} successfully`);
    } catch (error) {
      alert('Failed to update test status');
    } finally {
      setProcessingTest(null);
    }
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this test? This will also delete all submissions and cannot be undone.')) {
      return;
    }

    setProcessingTest(testId);
    try {
      await axios.delete(`${API_BASE_URL}/api/tests/${testId}`);
      setAllTests(allTests.filter(test => test._id !== testId));
      alert('Test deleted successfully');
    } catch (error) {
      alert('Failed to delete test');
    } finally {
      setProcessingTest(null);
    }
  };

  const handleDownloadPDF = async (test) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${test._id}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${test.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download PDF');
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download PDF');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'active':
        return { color: '#28a745', text: 'Active', textColor: 'white' };
      case 'closed':
        return { color: '#ffc107', text: 'Closed', textColor: 'black' };
      case 'inactive':
        return { color: '#6c757d', text: 'Inactive', textColor: 'white' };
      default:
        return { color: '#6c757d', text: 'Unknown', textColor: 'white' };
    }
  };

  // Filter function for tests
  const filterTests = () => {
    let filtered = allTests;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(test => 
        test.title.toLowerCase().includes(query) ||
        test.description.toLowerCase().includes(query) ||
        test.week.toString().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(test => test.status === 'active');
    } else if (statusFilter === 'closed') {
      filtered = filtered.filter(test => test.status === 'closed');
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(test => test.status === 'inactive');
    }

    return filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  };

  const filteredTests = filterTests();
  const activeTests = allTests.filter(test => test.status === 'active');
  const closedTests = allTests.filter(test => test.status === 'closed');
  const inactiveTests = allTests.filter(test => test.status === 'inactive');

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1>Manage Mocks</h1>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Link to="/admin/create-test" className="btn btn-primary">
              Create New Test
            </Link>
            <Link to="/admin" className="btn btn-secondary">
              Back to Dashboard
            </Link>
          </div>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: '20px' }}>
            {error}
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-3" style={{ marginBottom: '24px' }}>
          <div className="card">
            <h3 style={{ margin: '0 0 8px 0', color: '#007bff' }}>
              {allTests.length}
            </h3>
            <p style={{ margin: 0, color: '#666' }}>Total Tests</p>
          </div>
          <div className="card">
            <h3 style={{ margin: '0 0 8px 0', color: '#28a745' }}>
              {activeTests.length}
            </h3>
            <p style={{ margin: 0, color: '#666' }}>Active Tests</p>
            <div style={{ fontSize: '12px', color: '#ffc107', marginTop: '4px' }}>
              {closedTests.length} Closed
            </div>
          </div>
          <div className="card">
            <h3 style={{ margin: '0 0 8px 0', color: '#6c757d' }}>
              {inactiveTests.length}
            </h3>
            <p style={{ margin: 0, color: '#666' }}>Inactive Tests</p>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {/* Search Bar */}
          <div style={{ flex: '1', minWidth: '300px', position: 'relative' }}>
            <input
              type="text"
              placeholder="Search tests by title, description, or week..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px 12px 40px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            />
            <svg 
              style={{ 
                position: 'absolute', 
                left: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                width: '16px',
                height: '16px',
                color: '#666'
              }}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="21 21l-4.35-4.35"></path>
            </svg>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '12px 16px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: 'white',
              minWidth: '150px'
            }}
          >
            <option value="all">All Tests</option>
            <option value="active">Active Only</option>
            <option value="closed">Closed Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>

        {/* Results Counter */}
        {(searchQuery || statusFilter !== 'all') && (
          <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
            Showing {filteredTests.length} of {allTests.length} tests
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                }}
                style={{ 
                  marginLeft: '12px', 
                  color: '#007bff', 
                  background: 'none', 
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '14px'
                }}
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tests List */}
      <div className="card">
        <h2>Test History</h2>
        
        {filteredTests.length === 0 ? (
          <div className="text-center" style={{ padding: '40px', color: '#6c757d' }}>
            <h3>{searchQuery || statusFilter !== 'all' ? 'No Matching Tests' : 'No Tests Created'}</h3>
            <p>
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your search terms or filters.'
                : 'Create your first mock test to get started.'
              }
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <Link to="/admin/create-test" className="btn btn-primary">
                Create Test
              </Link>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {filteredTests.map((test) => {
              const statusInfo = getStatusInfo(test.status);
              return (
              <div key={test._id} className="card" style={{ 
                backgroundColor: test.status === 'active' ? 'white' : '#f8f9fa',
                border: test.status === 'active' ? '1px solid #ddd' : '1px solid #e9ecef'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                  <div>
                    <h3 style={{ 
                      margin: '0 0 8px 0', 
                      color: test.status === 'active' ? '#333' : '#6c757d' 
                    }}>
                      {test.title}
                    </h3>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                      <span className="badge badge-secondary">
                        Week {test.week}
                      </span>
                      <span 
                        className="badge" 
                        style={{ 
                          backgroundColor: statusInfo.color, 
                          color: statusInfo.textColor 
                        }}
                      >
                        {statusInfo.text}
                      </span>
                      {test.status === 'inactive' && (
                        <span style={{ fontSize: '12px', color: '#dc3545' }}>
                          ⚠ Hidden from students
                        </span>
                      )}
                      {test.status === 'closed' && (
                        <span style={{ fontSize: '12px', color: '#ffc107' }}>
                          📚 Read-only for students
                        </span>
                      )}
                    </div>
                    {test.description && (
                      <div style={{ color: test.status === 'active' ? '#666' : '#999', margin: '0 0 12px 0' }}>
                        <LaTeXRenderer text={test.description} />
                      </div>
                    )}
                  </div>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                  <div><strong>Problems:</strong> {test.problems?.length || 0}</div>
                  <div><strong>Due Date:</strong> {formatDate(test.dueDate)}</div>
                  <div><strong>Created:</strong> {formatDate(test.createdAt)}</div>
                  {test.updatedAt && test.updatedAt !== test.createdAt && (
                    <div><strong>Last Modified:</strong> {formatDate(test.updatedAt)}</div>
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <Link 
                    to={`/admin/grade/${test._id}`} 
                    className="btn btn-primary"
                    style={{ fontSize: '12px', padding: '6px 12px' }}
                  >
                    Grade Submissions
                  </Link>
                  
                  <Link 
                    to={`/admin/edit-test/${test._id}`} 
                    className="btn btn-success"
                    style={{ fontSize: '12px', padding: '6px 12px' }}
                  >
                    Edit
                  </Link>
                  
                  <button
                    onClick={() => handleDownloadPDF(test)}
                    className="btn btn-outline"
                    style={{ fontSize: '12px', padding: '6px 12px' }}
                    disabled={processingTest === test._id}
                  >
                    Download PDF
                  </button>
                  
                  <select
                    value={test.status}
                    onChange={(e) => handleUpdateStatus(test._id, e.target.value)}
                    disabled={processingTest === test._id}
                    style={{
                      fontSize: '12px',
                      padding: '6px 8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      minWidth: '100px'
                    }}
                  >
                    <option value="active">🟢 Active</option>
                    <option value="closed">🟡 Closed</option>
                    <option value="inactive">🔴 Inactive</option>
                  </select>
                  
                  {processingTest === test._id && (
                    <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                      Updating...
                    </span>
                  )}
                  
                  {/* Delete Button - Only for Super Admins */}
                  {user && user.role === 'super-admin' && (
                    <button
                      onClick={() => handleDeleteTest(test._id)}
                      className="btn btn-danger"
                      style={{ 
                        fontSize: '12px', 
                        padding: '6px 12px',
                        fontWeight: 'bold'
                      }}
                      disabled={processingTest === test._id}
                      title="Permanently delete this test and all associated submissions"
                    >
                      {processingTest === test._id ? (
                        <span>
                          <span style={{ marginRight: '4px' }}>🔄</span>
                          Deleting...
                        </span>
                      ) : (
                        <span>
                          <span style={{ marginRight: '4px' }}>🗑️</span>
                          Delete
                        </span>
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageMocks;
